import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Plus, Edit, Trash2, Save, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const COLORS = ['#3B82F6', '#22C55E', '#EAB308', '#F97316', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

export default function AdminAreas() {
  const [areas, setAreas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', color: '#3B82F6', description: '', image_url: '', sort_order: 0, active: true, online_bookable: true });

  const load = async () => {
    const r = await api.get('/areas');
    setAreas(r.data);
  };
  useEffect(() => { load(); }, []);

  const startEdit = (a) => {
    setEditing(a);
    setForm({ name: a.name, color: a.color, description: a.description || '', image_url: a.image_url || '', sort_order: a.sort_order, active: a.active, online_bookable: a.online_bookable });
    setShowForm(true);
  };
  const startNew = () => {
    setEditing(null);
    setForm({ name: '', color: COLORS[0], description: '', image_url: '', sort_order: areas.length, active: true, online_bookable: true });
    setShowForm(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error('Bild zu groß (max. 1 MB)'); e.target.value = ''; return; }
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, image_url: reader.result }));
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name erforderlich'); return; }
    try {
      if (editing) {
        await api.put(`/areas/${editing.id}`, form);
        toast.success('Aktualisiert');
      } else {
        await api.post('/areas', form);
        toast.success('Erstellt');
      }
      setShowForm(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };
  const del = async (a) => {
    if (!window.confirm(`Bereich "${a.name}" löschen?`)) return;
    try {
      await api.delete(`/areas/${a.id}`);
      toast.success('Gelöscht');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };
  const [deactivateModal, setDeactivateModal] = useState(null); // {area, preview, mode, result, loading}
  const [dayCloseModal, setDayCloseModal] = useState(null); // {area, form: {date, mode, start_time, end_time, reason}, result, loading}
  const [dayClosures, setDayClosures] = useState([]); // active future day-closures across all areas

  const loadClosures = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const r = await api.get(`/area-day-closures?date_from=${today}`);
      setDayClosures(r.data || []);
    } catch {
      setDayClosures([]);
    }
  };
  useEffect(() => { loadClosures(); }, []);

  const startDeactivate = async (a) => {
    setDeactivateModal({ area: a, preview: null, mode: 'auto_rebook', result: null, loading: true });
    try {
      const r = await api.get(`/areas/${a.id}/deactivate-preview`);
      setDeactivateModal({ area: a, preview: r.data, mode: 'auto_rebook', result: null, loading: false });
    } catch (e) {
      setDeactivateModal(null);
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const runDeactivate = async () => {
    if (!deactivateModal) return;
    setDeactivateModal({ ...deactivateModal, loading: true });
    try {
      const r = await api.post(`/areas/${deactivateModal.area.id}/deactivate`, {
        mode: deactivateModal.mode,
      });
      setDeactivateModal({ ...deactivateModal, result: r.data, loading: false });
      load();
    } catch (e) {
      setDeactivateModal({ ...deactivateModal, loading: false });
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const toggleActive = async (a) => {
    if (a.active) {
      // Deactivating: open intelligent dialog
      return startDeactivate(a);
    }
    await api.put(`/areas/${a.id}`, { active: true });
    toast.success('Bereich aktiviert');
    load();
  };

  const startDayClose = (a) => {
    const today = new Date().toISOString().slice(0, 10);
    setDayCloseModal({
      area: a,
      form: { date: today, date_to: '', mode: 'auto_rebook', start_time: '', end_time: '', reason: 'Tagessperre' },
      result: null, loading: false,
    });
  };

  const runDayClose = async () => {
    if (!dayCloseModal) return;
    const f = dayCloseModal.form;
    if (!f.date) { toast.error('Datum erforderlich'); return; }
    if (f.date_to && f.date_to < f.date) { toast.error('Enddatum muss nach dem Startdatum liegen'); return; }
    setDayCloseModal({ ...dayCloseModal, loading: true });
    try {
      const payload = { date: f.date, mode: f.mode, reason: f.reason };
      if (f.date_to && f.date_to !== f.date) payload.date_to = f.date_to;
      if (f.start_time) payload.start_time = f.start_time;
      if (f.end_time) payload.end_time = f.end_time;
      const r = await api.post(`/areas/${dayCloseModal.area.id}/close-day`, payload);
      setDayCloseModal({ ...dayCloseModal, result: r.data, loading: false });
      loadClosures();
    } catch (e) {
      setDayCloseModal({ ...dayCloseModal, loading: false });
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const removeClosure = async (c) => {
    const label = c.date_from ? `vom ${c.date_from} bis ${c.date_to}` : `vom ${c.date}`;
    if (!window.confirm(`Sperre ${label} entfernen?`)) return;
    try {
      await api.delete(`/area-day-closures/${c.id}`);
      toast.success('Tagessperre entfernt');
      loadClosures();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl" data-testid="admin-areas-page">
      <header className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C59]">Verwaltung</p>
          <h1 className="font-display text-3xl text-[#1C1C19]">Bereiche / Zonen</h1>
        </div>
        <button onClick={startNew} data-testid="area-add-btn" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-2 rounded text-sm flex items-center gap-2 transition">
          <Plus className="w-4 h-4" /> Bereich anlegen
        </button>
      </header>

      <div className="bg-white border border-[#E5E5E0] rounded-lg divide-y divide-[#E5E5E0]">
        {areas.length === 0 && <div className="p-8 text-center text-[#5C5C59]" data-testid="areas-empty">Noch keine Bereiche angelegt.</div>}
        {areas.map(a => {
          const aClosures = dayClosures.filter(c => c.area_id === a.id);
          return (
          <div key={a.id} className="p-4" data-testid={`area-row-${a.id}`}>
            <div className="flex items-center gap-4">
              <span className="w-4 h-4 rounded" style={{ background: a.color }}></span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-medium text-[#1C1C19]">{a.name}</h3>
                  {!a.active && <span className="text-xs px-2 py-0.5 bg-[#EF4444]/15 text-[#EF4444] rounded">Inaktiv</span>}
                  {!a.online_bookable && <span className="text-xs px-2 py-0.5 bg-[#9CA3AF]/15 text-[#5C5C59] rounded">Nur intern</span>}
                  {aClosures.length > 0 && <span className="text-xs px-2 py-0.5 bg-[#F97316]/15 text-[#F97316] rounded" data-testid={`area-closure-badge-${a.id}`}>{aClosures.length}× Tagessperre</span>}
                </div>
                {a.description && <p className="text-xs text-[#5C5C59] mt-0.5">{a.description}</p>}
              </div>
              <button onClick={() => startDayClose(a)} data-testid={`area-day-close-${a.id}`} className="text-xs px-3 py-1.5 border border-[#E5E5E0] rounded hover:border-[#F97316] hover:text-[#F97316]">
                Tag sperren
              </button>
              <button onClick={() => toggleActive(a)} data-testid={`area-toggle-${a.id}`} className="text-xs px-3 py-1.5 border border-[#E5E5E0] rounded hover:border-[#1C1C19]">
                {a.active ? 'Sperren' : 'Aktivieren'}
              </button>
              <button onClick={() => startEdit(a)} data-testid={`area-edit-${a.id}`} className="p-2 hover:bg-[#F9F9F7] rounded"><Edit className="w-4 h-4 text-[#5C5C59]" /></button>
              <button onClick={() => del(a)} data-testid={`area-delete-${a.id}`} className="p-2 hover:bg-[#FEE2E2] rounded"><Trash2 className="w-4 h-4 text-[#EF4444]" /></button>
            </div>
            {aClosures.length > 0 && (
              <ul className="mt-2 space-y-1 pl-8" data-testid={`area-closures-list-${a.id}`}>
                {aClosures.map(c => (
                  <li key={c.id} className="flex items-center gap-2 text-xs text-[#5C5C59]">
                    <span className="px-2 py-0.5 bg-[#F97316]/10 text-[#F97316] rounded font-mono">{c.date_from ? `${c.date_from} – ${c.date_to}` : c.date}</span>
                    {c.date_from && <span className="px-1.5 py-0.5 bg-[#1C1C19]/5 rounded">Zeitraum</span>}
                    {c.start_time && c.end_time && <span>{c.start_time}–{c.end_time}</span>}
                    <span className="capitalize">· {c.mode === 'auto_rebook' ? 'Auto-Umbuchung' : c.mode === 'manual' ? 'Manuell' : 'Nur neue gesperrt'}</span>
                    {c.reason && <span>· {c.reason}</span>}
                    <button onClick={() => removeClosure(c)} data-testid={`closure-remove-${c.id}`} className="ml-auto text-[#EF4444] hover:underline">Entfernen</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          );
        })}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-[#1C1C19]/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="area-form">
            <h2 className="font-display text-xl mb-4">{editing ? 'Bereich bearbeiten' : 'Neuer Bereich'}</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium block mb-1">Name</label>
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="area-form-name" className="w-full px-3 py-2 border border-[#E5E5E0] rounded focus:outline-none focus:border-[#1C1C19]" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Farbe</label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setForm({...form, color: c})} className={`w-8 h-8 rounded transition ${form.color === c ? 'ring-2 ring-offset-2 ring-[#1C1C19]' : ''}`} style={{ background: c }} data-testid={`area-form-color-${c}`} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Beschreibung (optional)</label>
                <input value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="area-form-desc" className="w-full px-3 py-2 border border-[#E5E5E0] rounded focus:outline-none focus:border-[#1C1C19]" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Foto (optional — wird Gästen bei der Buchung gezeigt)</label>
                {form.image_url ? (
                  <div className="relative">
                    <img src={form.image_url} alt="Bereich" className="w-full aspect-video object-cover rounded border border-[#E5E5E0]" data-testid="area-form-image-preview" />
                    <button onClick={() => setForm(f => ({ ...f, image_url: '' }))} data-testid="area-form-image-remove" className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1 shadow transition">
                      <X className="w-4 h-4 text-[#1C1C19]" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center gap-1 aspect-video border border-dashed border-[#E5E5E0] rounded cursor-pointer hover:border-[#1C1C19] text-[#5C5C59] text-sm transition" data-testid="area-form-image-upload-label">
                    <ImageIcon className="w-6 h-6" />
                    <span>Foto hochladen (quer/16:9, max. 1 MB)</span>
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="area-form-image-input" />
                  </label>
                )}
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Reihenfolge</label>
                <input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value) || 0})} data-testid="area-form-sort" className="w-full px-3 py-2 border border-[#E5E5E0] rounded focus:outline-none focus:border-[#1C1C19]" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} data-testid="area-form-active" />
                Aktiv
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.online_bookable} onChange={e => setForm({...form, online_bookable: e.target.checked})} data-testid="area-form-online" />
                Online buchbar
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowForm(false)} data-testid="area-form-cancel" className="px-4 py-2 border border-[#E5E5E0] rounded text-sm hover:border-[#1C1C19]">Abbrechen</button>
              <button onClick={save} data-testid="area-form-save" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-2 rounded text-sm">Speichern</button>
            </div>
          </div>
        </div>
      )}

      {deactivateModal && (
        <div className="fixed inset-0 bg-[#1C1C19]/40 z-50 flex items-center justify-center p-4" onClick={() => !deactivateModal.loading && setDeactivateModal(null)}>
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="area-deactivate-modal">
            {!deactivateModal.result ? (
              <>
                <h2 className="font-display text-xl mb-1">Bereich &bdquo;{deactivateModal.area.name}&ldquo; deaktivieren</h2>
                <p className="text-xs text-[#5C5C59] mb-4">
                  {deactivateModal.preview
                    ? <>Davon betroffen: <b>{deactivateModal.preview.affected_count}</b> Reservierung{deactivateModal.preview.affected_count === 1 ? '' : 'en'} ab heute.</>
                    : 'Lade Vorschau ...'}
                </p>
                <div className="space-y-2 mb-6">
                  {[
                    { v: 'lock_new_only', l: 'Nur neue Reservierungen sperren', d: 'Bestehende Reservierungen bleiben unverändert.' },
                    { v: 'auto_rebook', l: 'Automatische Umbuchung versuchen', d: 'System verschiebt Reservierungen in andere aktive Bereiche.' },
                    { v: 'manual', l: 'Manuelle Umbuchung', d: 'Bestehende Reservierungen werden als „Umbuchung erforderlich" markiert.' },
                  ].map(opt => (
                    <label key={opt.v} className={`block p-3 border rounded cursor-pointer transition ${deactivateModal.mode === opt.v ? 'border-[#1C1C19] bg-[#F9F9F7]' : 'border-[#E5E5E0] hover:border-[#9CA3AF]'}`} data-testid={`deactivate-mode-${opt.v}`}>
                      <div className="flex items-start gap-3">
                        <input
                          type="radio" name="dmode" value={opt.v}
                          checked={deactivateModal.mode === opt.v}
                          onChange={() => setDeactivateModal({ ...deactivateModal, mode: opt.v })}
                          className="mt-1"
                        />
                        <div>
                          <div className="text-sm font-medium">{opt.l}</div>
                          <div className="text-xs text-[#5C5C59]">{opt.d}</div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setDeactivateModal(null)} disabled={deactivateModal.loading} data-testid="deactivate-cancel" className="px-4 py-2 border border-[#E5E5E0] rounded text-sm hover:border-[#1C1C19] disabled:opacity-50">Abbrechen</button>
                  <button onClick={runDeactivate} disabled={deactivateModal.loading} data-testid="deactivate-confirm" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-2 rounded text-sm disabled:opacity-50">
                    {deactivateModal.loading ? 'Läuft...' : 'Deaktivieren'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="font-display text-xl mb-3">Ergebnis</h2>
                <div className="grid grid-cols-3 gap-3 mb-4 text-center">
                  <div className="p-3 bg-[#F9F9F7] rounded"><div className="text-2xl font-bold">{deactivateModal.result.affected_count}</div><div className="text-[10px] uppercase tracking-wider text-[#5C5C59]">Betroffen</div></div>
                  <div className="p-3 bg-[#22C55E]/10 rounded"><div className="text-2xl font-bold text-[#16A34A]" data-testid="result-moved">{deactivateModal.result.moved_count}</div><div className="text-[10px] uppercase tracking-wider text-[#16A34A]">Umgebucht</div></div>
                  <div className="p-3 bg-[#EF4444]/10 rounded"><div className="text-2xl font-bold text-[#EF4444]" data-testid="result-failed">{deactivateModal.result.failed_count}</div><div className="text-[10px] uppercase tracking-wider text-[#EF4444]">Manuell</div></div>
                </div>
                {deactivateModal.result.moved?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs uppercase tracking-wider text-[#16A34A] mb-2">Erfolgreich umgebucht</p>
                    <ul className="space-y-1 text-xs max-h-40 overflow-y-auto">
                      {deactivateModal.result.moved.map(m => (
                        <li key={m.reservation_id} className="p-2 bg-[#F9F9F7] rounded">
                          <b>{m.customer_name}</b> · {m.date} {m.start_time} · Tisch {m.old_table_number} → {m.new_area} Tisch {m.new_table_number}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {deactivateModal.result.failed?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs uppercase tracking-wider text-[#EF4444] mb-2">Brauchen manuelle Bearbeitung</p>
                    <ul className="space-y-1 text-xs max-h-40 overflow-y-auto">
                      {deactivateModal.result.failed.map(f => (
                        <li key={f.reservation_id} className="p-2 bg-[#FEE2E2] rounded">
                          <b>{f.customer_name}</b> · {f.date} {f.start_time} · {f.persons}P · {f.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex justify-end">
                  <button onClick={() => setDeactivateModal(null)} data-testid="deactivate-close" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-2 rounded text-sm">Fertig</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {dayCloseModal && (
        <div className="fixed inset-0 bg-[#1C1C19]/40 z-50 flex items-center justify-center p-4" onClick={() => !dayCloseModal.loading && setDayCloseModal(null)}>
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="area-dayclose-modal">
            {!dayCloseModal.result ? (
              <>
                <h2 className="font-display text-xl mb-1">Tag sperren &middot; &bdquo;{dayCloseModal.area.name}&ldquo;</h2>
                <p className="text-xs text-[#5C5C59] mb-4">
                  Bereich für einen einzelnen Tag oder einen Zeitraum sperren (z.B. „Terrasse im Winter geschlossen"). Optional auf eine Uhrzeit-Spanne begrenzen.
                </p>
                <div className="space-y-3 mb-5">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Von (Datum)</label>
                      <input type="date" value={dayCloseModal.form.date}
                             onChange={e => setDayCloseModal({...dayCloseModal, form: {...dayCloseModal.form, date: e.target.value}})}
                             data-testid="dayclose-date"
                             className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Bis (Datum, optional)</label>
                      <input type="date" value={dayCloseModal.form.date_to} min={dayCloseModal.form.date}
                             onChange={e => setDayCloseModal({...dayCloseModal, form: {...dayCloseModal.form, date_to: e.target.value}})}
                             data-testid="dayclose-date-to"
                             className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Grund (optional)</label>
                    <input value={dayCloseModal.form.reason}
                           onChange={e => setDayCloseModal({...dayCloseModal, form: {...dayCloseModal.form, reason: e.target.value}})}
                           data-testid="dayclose-reason"
                           className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Von (optional)</label>
                      <input type="time" value={dayCloseModal.form.start_time}
                             onChange={e => setDayCloseModal({...dayCloseModal, form: {...dayCloseModal.form, start_time: e.target.value}})}
                             data-testid="dayclose-start"
                             className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Bis (optional)</label>
                      <input type="time" value={dayCloseModal.form.end_time}
                             onChange={e => setDayCloseModal({...dayCloseModal, form: {...dayCloseModal.form, end_time: e.target.value}})}
                             data-testid="dayclose-end"
                             className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm" />
                    </div>
                  </div>
                  <p className="text-[10px] text-[#5C5C59]">Leer lassen = ganzer Tag</p>
                </div>
                <div className="space-y-2 mb-6">
                  {[
                    { v: 'block_new_only', l: 'Nur neue Reservierungen sperren', d: 'Bestehende Reservierungen an diesem Tag bleiben unverändert.' },
                    { v: 'auto_rebook', l: 'Automatische Umbuchung versuchen', d: 'System verschiebt betroffene Reservierungen in andere aktive Bereiche.' },
                    { v: 'manual', l: 'Manuelle Umbuchung', d: 'Betroffene Reservierungen werden als „Umbuchung erforderlich" markiert.' },
                  ].map(opt => (
                    <label key={opt.v} className={`block p-3 border rounded cursor-pointer transition ${dayCloseModal.form.mode === opt.v ? 'border-[#1C1C19] bg-[#F9F9F7]' : 'border-[#E5E5E0] hover:border-[#9CA3AF]'}`} data-testid={`dayclose-mode-${opt.v}`}>
                      <div className="flex items-start gap-3">
                        <input
                          type="radio" name="dcmode" value={opt.v}
                          checked={dayCloseModal.form.mode === opt.v}
                          onChange={() => setDayCloseModal({...dayCloseModal, form: {...dayCloseModal.form, mode: opt.v}})}
                          className="mt-1"
                        />
                        <div>
                          <div className="text-sm font-medium">{opt.l}</div>
                          <div className="text-xs text-[#5C5C59]">{opt.d}</div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setDayCloseModal(null)} disabled={dayCloseModal.loading} data-testid="dayclose-cancel" className="px-4 py-2 border border-[#E5E5E0] rounded text-sm hover:border-[#1C1C19] disabled:opacity-50">Abbrechen</button>
                  <button onClick={runDayClose} disabled={dayCloseModal.loading} data-testid="dayclose-confirm" className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 rounded text-sm disabled:opacity-50">
                    {dayCloseModal.loading ? 'Läuft...' : 'Tag sperren'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="font-display text-xl mb-3">Tagessperre angelegt</h2>
                <div className="grid grid-cols-3 gap-3 mb-4 text-center">
                  <div className="p-3 bg-[#F9F9F7] rounded"><div className="text-2xl font-bold">{dayCloseModal.result.affected_count}</div><div className="text-[10px] uppercase tracking-wider text-[#5C5C59]">Betroffen</div></div>
                  <div className="p-3 bg-[#22C55E]/10 rounded"><div className="text-2xl font-bold text-[#16A34A]" data-testid="dayclose-result-moved">{dayCloseModal.result.moved_count}</div><div className="text-[10px] uppercase tracking-wider text-[#16A34A]">Umgebucht</div></div>
                  <div className="p-3 bg-[#EF4444]/10 rounded"><div className="text-2xl font-bold text-[#EF4444]" data-testid="dayclose-result-failed">{dayCloseModal.result.failed_count}</div><div className="text-[10px] uppercase tracking-wider text-[#EF4444]">Manuell</div></div>
                </div>
                {dayCloseModal.result.moved?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs uppercase tracking-wider text-[#16A34A] mb-2">Erfolgreich umgebucht</p>
                    <ul className="space-y-1 text-xs max-h-40 overflow-y-auto">
                      {dayCloseModal.result.moved.map(m => (
                        <li key={m.reservation_id} className="p-2 bg-[#F9F9F7] rounded">
                          <b>{m.customer_name}</b> · {m.start_time} · Tisch {m.old_table_number} → {m.new_area} Tisch {m.new_table_number}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {dayCloseModal.result.failed?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs uppercase tracking-wider text-[#EF4444] mb-2">Brauchen manuelle Bearbeitung</p>
                    <ul className="space-y-1 text-xs max-h-40 overflow-y-auto">
                      {dayCloseModal.result.failed.map(f => (
                        <li key={f.reservation_id} className="p-2 bg-[#FEE2E2] rounded">
                          <b>{f.customer_name}</b> · {f.start_time} · {f.persons}P · {f.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex justify-end">
                  <button onClick={() => setDayCloseModal(null)} data-testid="dayclose-close" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-2 rounded text-sm">Fertig</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
